/**
 * Reads real answers from a note QuestionnaireResponse (replaces placeholder SOAP on the chart).
 */

import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import type { ClinicalNote } from '../../pages/clients/data/types';

export interface NoteContentSection {
  /** Questionnaire item text where available, otherwise the response item text or its linkId. */
  label: string;
  text: string;
}

type SoapKey = keyof NonNullable<ClinicalNote['soapNote']>;

/** linkIds written by the chart-note composer and the seeded note templates. */
const SOAP_LINK_IDS: Readonly<Record<string, SoapKey>> = {
  q1: 'subjective',
  body: 'subjective',
  content: 'subjective',
  subjective: 'subjective',
  q2: 'objective',
  objective: 'objective',
  review: 'objective',
  q3: 'assessment',
  assessment: 'assessment',
  q4: 'plan',
  plan: 'plan',
};

/** linkIds that carry the note title rather than note content. */
const TITLE_LINK_IDS: ReadonlySet<string> = new Set(['title', 'q0']);

function answerToText(answer: QuestionnaireResponseItemAnswer): string {
  if (answer.valueString !== undefined) return answer.valueString;
  if (answer.valueCoding) return answer.valueCoding.display ?? answer.valueCoding.code ?? '';
  if (answer.valueBoolean !== undefined) return answer.valueBoolean ? 'Yes' : 'No';
  if (answer.valueInteger !== undefined) return String(answer.valueInteger);
  if (answer.valueDecimal !== undefined) return String(answer.valueDecimal);
  if (answer.valueDate !== undefined) return answer.valueDate;
  if (answer.valueDateTime !== undefined) return answer.valueDateTime;
  if (answer.valueTime !== undefined) return answer.valueTime;
  if (answer.valueUri !== undefined) return answer.valueUri;
  if (answer.valueQuantity) {
    return [answer.valueQuantity.value, answer.valueQuantity.unit].filter(Boolean).join(' ');
  }
  return '';
}

function itemText(item: QuestionnaireResponseItem): string {
  return (item.answer ?? [])
    .map(answerToText)
    .filter((text) => text.trim().length > 0)
    .join('\n');
}

function collectQuestionnaireLabels(
  items: QuestionnaireItem[] | undefined,
  into: Map<string, string>,
): Map<string, string> {
  for (const item of items ?? []) {
    if (item.text) into.set(item.linkId, item.text);
    collectQuestionnaireLabels(item.item, into);
  }
  return into;
}

function walkResponseItems(
  items: QuestionnaireResponseItem[] | undefined,
  visit: (item: QuestionnaireResponseItem) => void,
): void {
  for (const item of items ?? []) {
    visit(item);
    walkResponseItems(item.item, visit);
    for (const answer of item.answer ?? []) {
      walkResponseItems(answer.item, visit);
    }
  }
}

export interface ExtractedNoteContent {
  contentSections: NoteContentSection[];
  /** Undefined when the response has no answered content. */
  soapNote: ClinicalNote['soapNote'] | undefined;
}

/**
 * One walk of `qr.item[]`: labeled sections for "Show Note", plus a best-effort SOAP shape.
 * Title-only items are skipped. Known SOAP linkIds map to fields; anything else goes under subjective.
 */
export function extractNoteContent(
  qr: QuestionnaireResponse | undefined,
  questionnaire?: Questionnaire,
): ExtractedNoteContent {
  if (!qr) return { contentSections: [], soapNote: undefined };

  const labels = collectQuestionnaireLabels(questionnaire?.item, new Map());
  const contentSections: NoteContentSection[] = [];
  const soap: Record<SoapKey, string[]> = {
    subjective: [],
    objective: [],
    assessment: [],
    plan: [],
  };
  let answered = false;

  walkResponseItems(qr.item, (item) => {
    if (TITLE_LINK_IDS.has(item.linkId)) return;
    const text = itemText(item);
    if (!text.trim()) return;
    answered = true;
    const label = labels.get(item.linkId) ?? item.text ?? item.linkId;
    contentSections.push({ label, text });
    const key = SOAP_LINK_IDS[item.linkId];
    if (key) {
      soap[key].push(text);
      return;
    }
    const soapLabel = labels.get(item.linkId) ?? item.text;
    soap.subjective.push(soapLabel ? `${soapLabel}\n${text}` : text);
  });

  return {
    contentSections,
    soapNote: answered
      ? {
          subjective: soap.subjective.join('\n\n'),
          objective: soap.objective.join('\n\n'),
          assessment: soap.assessment.join('\n\n'),
          plan: soap.plan.join('\n\n'),
        }
      : undefined,
  };
}

export function extractNoteContentSections(
  qr: QuestionnaireResponse | undefined,
  questionnaire?: Questionnaire,
): NoteContentSection[] {
  return extractNoteContent(qr, questionnaire).contentSections;
}

export function extractSoapNoteFromQr(
  qr: QuestionnaireResponse | undefined,
  questionnaire?: Questionnaire,
): ClinicalNote['soapNote'] | undefined {
  return extractNoteContent(qr, questionnaire).soapNote;
}
